<template>
  <div class="page-class page-center page-escalation-matrix-form">
    <div class="page-staff-form">
      <h1 class="page-title text-center">{{ title }}</h1>

      <div v-if="isEdit && loading && !formReady" class="text-center py-4">
        <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
        <p class="mt-2 mb-0">Loading…</p>
      </div>

      <b-alert v-else-if="isEdit && !loading && !form" show variant="warning" class="box-shadow2">
        Could not load matrix “{{ routeKey }}”. Check the key or your permissions, then go back to the list.
        <b-button variant="outline-secondary" size="sm" class="ml-2" @click="onCancel">Back to list</b-button>
      </b-alert>

      <b-card v-else-if="form" class="box-shadow2" :class="{ 'bg-dark text-light border-secondary': isDarkMode }">
        <b-form @submit.prevent="onSubmit">
          <b-form-group>
            <b-form-checkbox v-model="form.isActive" switch :disabled="saving || isEdit"> Active </b-form-checkbox>
          </b-form-group>

          <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div>
              <h5 class="mb-1">Departments &amp; escalation levels</h5>
              <p class="mb-0 small text-muted">
                Each department escalates in order:
                <span class="flow-legend-inline">
                  <span class="legend-pill legend-pill--l1">L1</span>
                  <font-awesome-icon :icon="['fas', 'arrow-right']" class="legend-arrow" />
                  <span class="legend-pill legend-pill--l2">L2</span>
                  <font-awesome-icon :icon="['fas', 'arrow-right']" class="legend-arrow" />
                  <span class="legend-pill legend-pill--l3">L3</span>
                </span>
              </p>
            </div>
            <div class="d-flex flex-wrap">
              <b-button
                type="button"
                size="sm"
                variant="outline-success"
                class="mr-2 mb-1"
                :disabled="saving || !form.matrix.length"
                @click="onDownloadCurrentMatrix"
              >
                <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
                Download current matrix
              </b-button>
              <b-button
                type="button"
                size="sm"
                variant="outline-secondary"
                class="mr-2 mb-1"
                :disabled="saving"
                @click="onBulkUpload"
              >
                <font-awesome-icon :icon="['fas', 'upload']" class="mr-1" />
                Bulk upload
              </b-button>
              <b-button type="button" size="sm" variant="outline-primary" class="mb-1" :disabled="saving || isEdit" @click="addRow">
                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                Add department
              </b-button>
            </div>
          </div>

          <div
            v-for="(row, idx) in form.matrix"
            :key="row._rowKey"
            class="dept-card box-shadow2"
            :class="{
              'dept-card--dark': isDarkMode,
              'dept-card--expanded': isDeptExpanded(row._rowKey, idx),
              'dept-card--collapsed': !isDeptExpanded(row._rowKey, idx)
            }"
          >
            <div class="dept-card__header">
              <div class="dept-card__header-main">
                <span class="dept-card__index">{{ idx + 1 }}</span>
                <div class="dept-card__summary-wrap">
                  <strong class="dept-card__label">{{ deptDisplayName(row) }}</strong>
                  <span class="dept-card__hint d-block small text-muted">
                    <span v-if="row.tat !== '' && row.tat != null" class="dept-card__tat-badge">TAT {{ row.tat }} days</span>
                    <span class="dept-card__levels-hint">L1 → L2 → L3</span>
                  </span>
                </div>
              </div>
              <div class="dept-card__header-actions">
                <b-button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  class="mr-1"
                  :disabled="saving || form.matrix.length < 2 || isEdit"
                  @click="removeRow(idx)"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                  Remove
                </b-button>
                <b-button
                  v-b-toggle="deptCollapseId(row)"
                  type="button"
                  size="sm"
                  :variant="isDarkMode ? 'outline-light' : 'outline-secondary'"
                  :disabled="saving"
                  :title="isDeptExpanded(row._rowKey, idx) ? 'Collapse' : 'Expand'"
                  :aria-label="isDeptExpanded(row._rowKey, idx) ? 'Collapse department' : 'Expand department'"
                  :aria-expanded="isDeptExpanded(row._rowKey, idx) ? 'true' : 'false'"
                  :aria-controls="deptCollapseId(row)"
                  class="dept-card__toggle-btn"
                >
                  <font-awesome-icon :icon="['fas', isDeptExpanded(row._rowKey, idx) ? 'chevron-up' : 'chevron-down']" />
                </b-button>
              </div>
            </div>

            <b-collapse
              :id="deptCollapseId(row)"
              v-model="expandedDepts[row._rowKey]"
              class="dept-card__body"
              :class="{ 'dept-card__body--dark': isDarkMode }"
              accordion="dept-matrix-accordion"
              @input="val => onDeptCollapseInput(row._rowKey, val)"
            >
            <div class="dept-card__meta">
              <b-row>
                <b-col cols="12" md="8">
                  <b-form-group label="Department name" :label-for="`dept-${idx}`" class="mb-md-0">
                    <b-form-input
                      :id="`dept-${idx}`"
                      v-model="row.department"
                      required
                      :disabled="saving || isEdit"
                      :class="inputClass"
                      placeholder="e.g. Payroll"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="TAT (days)" :label-for="`tat-${idx}`" class="mb-0">
                    <b-form-input
                      :id="`tat-${idx}`"
                      v-model.number="row.tat"
                      type="number"
                      min="0"
                      required
                      :disabled="saving"
                      :class="inputClass"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div class="escalation-pipeline" role="list" aria-label="Escalation levels L1 through L3">
              <template v-for="(level, levelIdx) in levelKeys">
                <div
                  v-if="levelIdx > 0"
                  :key="`${row._rowKey}-connector-${level}`"
                  class="pipeline-connector"
                  aria-hidden="true"
                >
                  <div class="pipeline-connector__line"></div>
                  <div class="pipeline-connector__badge">
                    <font-awesome-icon :icon="['fas', 'arrow-down']" class="d-md-none" />
                    <font-awesome-icon :icon="['fas', 'arrow-right']" class="d-none d-md-inline" />
                    <span class="pipeline-connector__text">escalates to</span>
                  </div>
                  <div class="pipeline-connector__line"></div>
                </div>

                <section
                  :key="`${row._rowKey}-${level}`"
                  class="level-card box-shadow3"
                  :class="[`level-card--${level.toLowerCase()}`, { 'level-card--dark': isDarkMode }]"
                  role="listitem"
                  :aria-label="`${levelMeta[level].label}: ${levelMeta[level].subtitle}`"
                >
                  <header class="level-card__header">
                    <div class="level-card__badge-wrap">
                      <span class="level-card__step">Step {{ levelMeta[level].step }} / 3</span>
                      <span class="level-card__badge">{{ level }}</span>
                    </div>
                    <div class="level-card__titles">
                      <h6 class="level-card__title mb-0">{{ levelMeta[level].label }}</h6>
                      <span class="level-card__subtitle">{{ levelMeta[level].subtitle }}</span>
                    </div>
                  </header>

                  <div class="level-card__role">
                    <b-form-group label="Role" :label-for="`${level}-role-${idx}`" class="mb-0">
                      <multiselect
                        :id="`${level}-role-${idx}`"
                        v-model="row[level].role"
                        :options="getRoleOptions(level)"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        :searchable="true"
                        :allow-empty="true"
                        placeholder="Select role"
                        :disabled="saving"
                        :class="['box-shadow2', { 'multiselect-dark': isDarkMode }]"
                      />
                    </b-form-group>
                  </div>

                  <div class="level-card__users">
                    <div class="level-card__users-label">
                      <font-awesome-icon :icon="['fas', 'users']" class="mr-1" />
                      Assignees at {{ level }}
                    </div>

                    <div
                      v-for="(user, uIdx) in row[level].users"
                      :key="`${row._rowKey}-${level}-user-${uIdx}`"
                      class="user-entry box-shadow2"
                      :class="{ 'user-entry--dark': isDarkMode }"
                    >
                      <div class="user-entry__header">
                        <span class="user-entry__tag">User {{ uIdx + 1 }}</span>
                        <b-button
                          type="button"
                          size="sm"
                          variant="link"
                          class="user-entry__remove p-0"
                          :disabled="saving"
                          @click="removeUser(row[level], uIdx)"
                        >
                          <font-awesome-icon :icon="['fas', 'times']" class="mr-1" />
                          Remove
                        </b-button>
                      </div>
                      <b-form-group label="Name *" :label-for="`${level}-name-${idx}-${uIdx}`" class="mb-2">
                        <b-form-input
                          :id="`${level}-name-${idx}-${uIdx}`"
                          v-model="user.name"
                          required
                          :disabled="saving"
                          :class="fieldClass(row._rowKey, level, uIdx, 'name')"
                        />
                      </b-form-group>
                      <b-form-group label="Mobile *" :label-for="`${level}-mobile-${idx}-${uIdx}`" class="mb-2">
                        <b-form-input
                          :id="`${level}-mobile-${idx}-${uIdx}`"
                          v-model="user.mobile"
                          type="tel"
                          required
                          :disabled="saving"
                          :class="fieldClass(row._rowKey, level, uIdx, 'mobile')"
                        />
                      </b-form-group>
                      <b-form-group label="Email *" :label-for="`${level}-email-${idx}-${uIdx}`" class="mb-2">
                        <b-form-input
                          :id="`${level}-email-${idx}-${uIdx}`"
                          v-model="user.email"
                          type="email"
                          required
                          :disabled="saving"
                          :class="fieldClass(row._rowKey, level, uIdx, 'email')"
                        />
                      </b-form-group>
                      <!-- Store multiselect replaced by semicolon-separated store codes -->
                      <!--
                      <b-form-group label="Stores" :label-for="`${level}-stores-${idx}-${uIdx}`" class="mb-0">
                        <multiselect
                          :id="`${level}-stores-${idx}-${uIdx}`"
                          v-model="user.stores"
                          :options="storeOptions"
                          label="text"
                          track-by="value"
                          :multiple="true"
                          :close-on-select="false"
                          :clear-on-select="false"
                          :preserve-search="true"
                          :searchable="true"
                          :taggable="true"
                          tag-placeholder="Add store id"
                          placeholder="Select stores"
                          :disabled="saving"
                          :class="['box-shadow2', { 'multiselect-dark': isDarkMode }]"
                          @tag="tag => addStoreTag(user, tag)"
                        />
                      </b-form-group>
                      -->
                      <b-form-group
                        label="Store codes"
                        :label-for="`${level}-storecodes-${idx}-${uIdx}`"
                        class="mb-0"
                        description="Enter store codes separated by ; (semicolon)"
                      >
                        <b-form-textarea
                          :id="`${level}-storecodes-${idx}-${uIdx}`"
                          v-model="user.storeCodes"
                          rows="3"
                          :disabled="saving"
                          :class="inputClass"
                          placeholder="e.g. 23;234;235"
                        />
                      </b-form-group>
                    </div>

                    <b-button
                      type="button"
                      size="sm"
                      :variant="isDarkMode ? 'outline-light' : 'outline-primary'"
                      class="level-card__add-user"
                      :disabled="saving"
                      @click="addUser(row[level])"
                    >
                      <font-awesome-icon :icon="['fas', 'user-plus']" class="mr-1" />
                      Add user at {{ level }}
                    </b-button>
                  </div>

                  <footer v-if="levelIdx < levelKeys.length - 1" class="level-card__footer">
                    <span class="level-card__footer-hint">
                      Unresolved after TAT → escalates to {{ levelKeys[levelIdx + 1] }}
                    </span>
                  </footer>
                </section>
              </template>
            </div>
            </b-collapse>
          </div>

          <div class="d-flex flex-wrap mt-4">
            <b-button type="submit" variant="success" class="mr-2 box-shadow2" :disabled="saving || loading">
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
              <font-awesome-icon v-else :icon="['fas', 'save']" class="mr-1" />
              {{ isEdit ? 'Update' : 'Create' }}
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              class="box-shadow2"
              :disabled="saving"
              @click="onCancel"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import csv from 'csvtojson';
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import {
  emptyMatrixRow,
  emptyUser,
  rowToForm,
  buildEscalationPayload,
  validateEscalationForm,
  userFieldKey,
  buildBulkFormFromUpload,
  matrixRowsFromBulkCsv,
  parseBulkEscalationJson,
  buildEscalationMatrixSampleCsv,
  buildEscalationMatrixSampleJson,
  matrixToBulkCsv,
  downloadTextFile
} from '@/helper/escalationMatrixForm';

export default {
  name: 'EscalationMatrixForm',
  components: {
    Multiselect
  },
  metaInfo() {
    return {
      title: this.title
    };
  },
  data() {
    return {
      form: null,
      saving: false,
      submitted: false,
      invalidFields: new Set(),
      expandedDepts: {},
      levelKeys: ['L1', 'L2', 'L3'],
      levelMeta: {
        L1: { label: 'Level 1 — First response', subtitle: 'Initial handler', step: 1 },
        L2: { label: 'Level 2 — Secondary', subtitle: 'Escalation if L1 TAT exceeded', step: 2 },
        L3: { label: 'Level 3 — Final', subtitle: 'CHRO / final authority', step: 3 }
      },
      roleCatalog: {
        L1: ['PAYROLL_L1', 'C&B_L1', 'InStore_L1'],
        L2: ['PAYROLL_L2', 'C&B_L2'],
        L3: ['CHRO']
      }
    };
  },
  computed: {
    ...mapState('qms', ['escalationMatrix', 'loading']),
    ...mapState('user', ['availableRoles']),
    ...mapState('darkMode', ['isDarkMode']),
    isEdit() {
      return this.$route.name === 'escalation-matrix-edit';
    },
    title() {
      return this.isEdit ? 'Edit escalation matrix' : 'New escalation matrix';
    },
    formReady() {
      return !!this.form;
    },
    routeKey() {
      return (this.$route.params.key || 'hr_management').trim();
    },
    inputClass() {
      return this.isDarkMode ? 'box-shadow3 bg-dark text-light border-secondary' : 'box-shadow3 bg-light text-dark';
    }
  },
  async mounted() {
    await this.getRoles();
    if (this.isEdit) {
      try {
        await this.listEscalationMatrix({ router: this.$router, key: this.routeKey });
        this.syncFromStore();
      } catch (e) {
        this.form = null;
      }
    } else {
      this.initNewForm();
    }
  },
  methods: {
    ...mapActions('qms', ['listEscalationMatrix', 'postEscalationMatrix', 'patchEscalationMatrix']),
    ...mapActions('user', ['getRoles']),
    fieldClass(rowKey, level, uIdx, field) {
      const invalid = this.submitted && this.invalidFields.has(userFieldKey(rowKey, level, uIdx, field));
      return [
        this.inputClass,
        { 'border-danger': invalid }
      ];
    },
    runValidation() {
      const result = validateEscalationForm(this.form);
      this.submitted = true;
      this.invalidFields = result.invalidKeys;
      return result;
    },
    deptCollapseId(row) {
      return `dept-collapse-${row._rowKey}`;
    },
    deptDisplayName(row) {
      const name = (row.department || '').trim();
      return name || 'Untitled department';
    },
    isDeptExpanded(rowKey, idx) {
      if (this.expandedDepts[rowKey] !== undefined) {
        return !!this.expandedDepts[rowKey];
      }
      return idx === 0;
    },
    onDeptCollapseInput(rowKey, visible) {
      if (visible) {
        Object.keys(this.expandedDepts).forEach(key => {
          if (key !== rowKey) {
            this.$set(this.expandedDepts, key, false);
          }
        });
      }
      this.$set(this.expandedDepts, rowKey, visible);
    },
    initExpandedDepts(openFirstOnly = true) {
      if (!this.form || !Array.isArray(this.form.matrix)) {
        return;
      }
      const next = { ...this.expandedDepts };
      let anyOpen = Object.values(next).some(Boolean);
      this.form.matrix.forEach((row, idx) => {
        if (next[row._rowKey] === undefined) {
          const shouldOpen = openFirstOnly ? idx === 0 && !anyOpen : true;
          next[row._rowKey] = shouldOpen;
          if (shouldOpen) {
            anyOpen = true;
          }
        }
      });
      this.expandedDepts = next;
    },
    getRoleOptions(level) {
      const fetched = (this.availableRoles || []).map(role => role.name).filter(Boolean);
      const base = fetched.length ? fetched : this.roleCatalog[level] || [];
      const existing = (this.form?.matrix || []).map(r => r?.[level]?.role).filter(Boolean);
      const unique = [...new Set([...base, ...existing])];
      return unique;
    },
    /*
    addStoreTag(user, tag) {
      const value = String(tag).trim();
      if (!value) {
        return;
      }
      const asNum = Number(value);
      const normalized = Number.isFinite(asNum) && !Number.isNaN(asNum) ? asNum : value;
      const entry = { text: String(normalized), value: normalized };
      if (!user.stores.some(s => String(s.value) === String(entry.value))) {
        user.stores.push(entry);
      }
    },
    */
    addUser(level) {
      level.users.push(emptyUser());
    },
    removeUser(level, index) {
      if (level.users.length <= 1) {
        this.$set(level.users, 0, emptyUser());
        return;
      }
      level.users.splice(index, 1);
    },
    initNewForm() {
      this.expandedDepts = {};
      this.form = {
        key: 'hr_management',
        isActive: true,
        matrix: [emptyMatrixRow(0)]
      };
      this.initExpandedDepts(true);
    },
    syncFromStore() {
      const doc = this.escalationMatrix;
      if (!doc) {
        this.form = null;
        return;
      }
      const matrix = Array.isArray(doc.matrix) ? doc.matrix.map((r, idx) => rowToForm(r, idx)) : [];
      this.form = {
        key: doc.key || this.routeKey,
        isActive: !!doc.isActive,
        matrix: matrix.length ? matrix : [emptyMatrixRow(0)]
      };
      this.initExpandedDepts(true);
    },
    addRow() {
      const next = this.form.matrix.length;
      const row = emptyMatrixRow(next);
      this.form.matrix.push(row);
      Object.keys(this.expandedDepts).forEach(key => {
        this.$set(this.expandedDepts, key, false);
      });
      this.$set(this.expandedDepts, row._rowKey, true);
    },
    removeRow(index) {
      if (this.form.matrix.length < 2) {
        return;
      }
      const removed = this.form.matrix[index];
      this.form.matrix.splice(index, 1);
      if (removed && removed._rowKey) {
        this.$delete(this.expandedDepts, removed._rowKey);
      }
      this.initExpandedDepts(true);
    },
    onCancel() {
      this.$router.push('/qms/escalation-matrix');
    },
    onDownloadCurrentMatrix() {
      if (!this.form || !Array.isArray(this.form.matrix) || !this.form.matrix.length) {
        return;
      }
      const key = (this.form.key || 'hr_management').replace(/[^\w-]+/g, '_');
      downloadTextFile({
        content: matrixToBulkCsv(this.form.matrix),
        filename: `escalation-matrix-${key}.csv`,
        mimeType: 'text/csv;charset=utf-8'
      });
    },
    downloadSampleCsv() {
      downloadTextFile({
        content: buildEscalationMatrixSampleCsv(),
        filename: 'escalation-matrix-bulk-sample.csv',
        mimeType: 'text/csv;charset=utf-8'
      });
    },
    downloadSampleJson() {
      downloadTextFile({
        content: buildEscalationMatrixSampleJson(),
        filename: 'escalation-matrix-bulk-sample.json',
        mimeType: 'application/json;charset=utf-8'
      });
    },
    async parseBulkFile(file) {
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read the selected file.'));
        reader.readAsText(file);
      });

      if (extension === 'json') {
        const parsed = parseBulkEscalationJson(text);
        return { matrix: parsed.matrix, key: parsed.key, isActive: parsed.isActive };
      }

      if (extension === 'csv') {
        const rows = await csv().fromString(text);
        return { matrix: matrixRowsFromBulkCsv(rows) };
      }

      throw new Error('Only CSV and JSON files are supported.');
    },
    async onBulkUpload() {
      const allowedExtensions = ['csv', 'json'];
      const result = await Vue.swal({
        title: 'Bulk upload escalation matrix',
        html:
          '<p class="mb-2">Upload a CSV or JSON file to populate departments and escalation levels.</p>' +
          '<p class="small text-muted mb-0">CSV: one row per user (Department, TAT, Level, Role, Name, Mobile, Email, Stores). ' +
          'Stores use semicolon (;) separators. JSON: full matrix document as returned by the API.</p>' +
          '<p class="small text-muted mb-0">Upload saves directly via the same update API as the form.</p>',
        input: 'file',
        inputAttributes: {
          accept: '.csv,.json',
          'aria-label': 'Upload escalation matrix file'
        },
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Upload',
        showCancelButton: true,
        cancelButtonText: 'Close',
        showDenyButton: true,
        denyButtonText: 'Download CSV sample',
        denyButtonColor: '#28a745',
        showCloseButton: true,
        footer:
          '<button type="button" id="escalation-json-sample" class="btn btn-link btn-sm p-0">Download JSON sample</button>',
        background: this.isDarkMode ? '#464d54' : '#ffffff',
        customClass: {
          title: this.isDarkMode ? 'text-light' : 'text-dark',
          content: this.isDarkMode ? 'text-light' : 'text-dark',
          footer: this.isDarkMode ? 'text-light' : 'text-dark'
        },
        didOpen: () => {
          const jsonBtn = document.getElementById('escalation-json-sample');
          if (jsonBtn) {
            jsonBtn.addEventListener('click', e => {
              e.preventDefault();
              this.downloadSampleJson();
            });
          }
        },
        preConfirm: file => {
          if (!file) {
            Vue.swal.showValidationMessage('Please choose a file.');
            return false;
          }
          const extension = (file.name.split('.').pop() || '').toLowerCase();
          if (!allowedExtensions.includes(extension)) {
            Vue.swal.showValidationMessage('Only CSV and JSON files are allowed.');
            return false;
          }
          return file;
        }
      });

      if (result.isDenied) {
        this.downloadSampleCsv();
        return;
      }

      if (!result.isConfirmed || !result.value) {
        return;
      }

      try {
        const { matrix, key, isActive } = await this.parseBulkFile(result.value);
        if (!matrix.length) {
          throw new Error('No departments were found in the uploaded file.');
        }

        const bulkForm = buildBulkFormFromUpload(this.form, { matrix, key, isActive });
        const validation = validateEscalationForm(bulkForm);
        if (!validation.valid) {
          this.form = bulkForm;
          this.submitted = true;
          this.invalidFields = validation.invalidKeys;
          this.initExpandedDepts(false);
          throw new Error(validation.message || 'Name, mobile, and email are required for each assignee.');
        }

        const confirmUpload = await Vue.swal({
          title: 'Upload and update matrix?',
          text: `This will replace the matrix with ${matrix.length} department(s) from ${result.value.name} and save immediately.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Upload & save',
          cancelButtonText: 'Cancel',
          background: this.isDarkMode ? '#464d54' : '#ffffff',
          customClass: {
            title: this.isDarkMode ? 'text-light' : 'text-dark',
            content: this.isDarkMode ? 'text-light' : 'text-dark'
          }
        });
        if (!confirmUpload.isConfirmed) {
          return;
        }

        const payloadKey = (key || bulkForm.key || this.routeKey).trim();
        bulkForm.key = payloadKey;
        const payload = buildEscalationPayload(bulkForm);
        this.saving = true;
        const redirectUrl = '/qms/escalation-matrix';
        try {
          if (this.isEdit && this.escalationMatrix && this.escalationMatrix.id) {
            await this.patchEscalationMatrix({
              payload,
              router: this.$router,
              key: payloadKey,
              redirectUrl
            });
          } else {
            await this.postEscalationMatrix({
              payload,
              router: this.$router,
              key: payloadKey,
              redirectUrl
            });
          }
        } finally {
          this.saving = false;
        }
      } catch (e) {
        Vue.swal({
          title: 'Upload failed',
          text: (e && e.message) || 'Could not parse the uploaded file.',
          icon: 'error',
          confirmButtonText: 'Ok',
          background: this.isDarkMode ? '#464d54' : '#ffffff',
          customClass: {
            title: this.isDarkMode ? 'text-light' : 'text-dark',
            content: this.isDarkMode ? 'text-light' : 'text-dark'
          }
        });
      }
    },
    async onSubmit() {
      const key = (this.form && this.form.key) || this.routeKey;
      if (!this.form || !key) {
        return;
      }
      const validation = this.runValidation();
      if (!validation.valid) {
        this.initExpandedDepts(false);
        return;
      }
      this.form.key = key;
      const payload = buildEscalationPayload(this.form);
      this.saving = true;
      const redirectUrl = '/qms/escalation-matrix';
      try {
        if (this.isEdit && this.escalationMatrix && this.escalationMatrix.id) {
          await this.patchEscalationMatrix({
            payload,
            router: this.$router,
            key,
            redirectUrl
          });
        } else {
          await this.postEscalationMatrix({
            payload,
            router: this.$router,
            key,
            redirectUrl
          });
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* ---- Legend ---- */
.flow-legend-inline {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-left: 0.25rem;
  vertical-align: middle;
}

.legend-pill {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 2px solid transparent;
}

.legend-pill--l1 {
  color: #0d6e4f;
  background: #d8f3e9;
  border-color: #3dbb8a;
}

.legend-pill--l2 {
  color: #9a5f00;
  background: #fff3d6;
  border-color: #f0b429;
}

.legend-pill--l3 {
  color: #5b2d82;
  background: #f0e4fa;
  border-color: #9b6dd7;
}

.legend-arrow {
  font-size: 0.65rem;
  opacity: 0.55;
}

/* ---- Department card (outermost) ---- */
.dept-card {
  margin-bottom: 2rem;
  border-radius: 12px;
  border: 2px solid #dee2e6;
  background: #f8f9fb;
  overflow: hidden;
}

.dept-card--dark {
  background: #2b3035;
  border-color: #495057;
}

.dept-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
  border-bottom: 2px solid #ced4da;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.dept-card--collapsed .dept-card__header {
  border-bottom: none;
  box-shadow: none;
}

.dept-card--expanded .dept-card__header {
  border-bottom: 2px solid #ced4da;
}

.dept-card__header-main {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1;
  min-width: 0;
}

.dept-card__header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.dept-card__toggle-btn {
  min-width: 2.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.dept-card__summary-wrap {
  min-width: 0;
}

.dept-card__tat-badge {
  display: inline-block;
  margin-right: 0.5rem;
  padding: 0.1rem 0.45rem;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 4px;
  background: #e7f1ff;
  color: #0d6efd;
}

.dept-card--dark .dept-card__tat-badge {
  background: rgba(13, 110, 253, 0.25);
  color: #9ec5fe;
}

.dept-card__levels-hint {
  margin-right: 0.35rem;
}

.dept-card__body {
  border-top: 1px solid #dee2e6;
}

.dept-card__body--dark {
  border-top-color: #495057;
}

.dept-card--dark .dept-card__header {
  background: linear-gradient(135deg, #343a40 0%, #3d444b 100%);
  border-bottom-color: #495057;
}

.dept-card__index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1rem;
  color: #fff;
  background: #495057;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.dept-card--dark .dept-card__index {
  background: #6c757d;
}

.dept-card__label {
  font-size: 1.05rem;
}

.dept-card__meta {
  padding: 1rem 1.25rem 0.5rem;
  border-bottom: 1px dashed #ced4da;
}

.dept-card--dark .dept-card__meta {
  border-bottom-color: #495057;
}

/* ---- Pipeline: vertical mobile, horizontal desktop ---- */
.escalation-pipeline {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 1.25rem;
  gap: 0;
}

@media (min-width: 992px) {
  .escalation-pipeline {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 0;
    padding: 1.25rem 1rem 1.5rem;
  }
}

.pipeline-connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0;
  flex-shrink: 0;
}

@media (min-width: 992px) {
  .pipeline-connector {
    flex-direction: row;
    padding: 0 0.35rem;
    align-self: center;
    min-width: 4.5rem;
    max-width: 5.5rem;
  }
}

.pipeline-connector__line {
  flex: 1;
  width: 2px;
  min-height: 1rem;
  background: linear-gradient(180deg, transparent, #adb5bd 20%, #adb5bd 80%, transparent);
}

@media (min-width: 992px) {
  .pipeline-connector__line {
    width: auto;
    height: 2px;
    min-height: 0;
    min-width: 0.5rem;
    background: linear-gradient(90deg, transparent, #adb5bd 15%, #adb5bd 85%, transparent);
  }
}

.pipeline-connector__badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.35rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  white-space: nowrap;
}

.dept-card--dark .pipeline-connector__badge {
  background: #495057;
  border-color: #6c757d;
  color: #dee2e6;
}

.pipeline-connector__text {
  font-size: 0.6rem;
}

/* ---- Level cards ---- */
.level-card {
  flex: 1 1 0;
  min-width: 0;
  border-radius: 10px;
  border: 2px solid transparent;
  padding: 0;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

@media (min-width: 992px) {
  .level-card {
    margin-bottom: 0;
    min-height: 320px;
  }
}

.level-card--dark {
  background: #343a40;
}

.level-card--l1 {
  border-color: #3dbb8a;
  box-shadow: 0 4px 14px rgba(61, 187, 138, 0.25), inset 4px 0 0 #3dbb8a;
}

.level-card--l2 {
  border-color: #f0b429;
  box-shadow: 0 4px 14px rgba(240, 180, 41, 0.28), inset 4px 0 0 #f0b429;
}

.level-card--l3 {
  border-color: #9b6dd7;
  box-shadow: 0 4px 14px rgba(155, 109, 215, 0.28), inset 4px 0 0 #9b6dd7;
}

.level-card--dark.level-card--l1 {
  box-shadow: 0 4px 14px rgba(61, 187, 138, 0.15), inset 4px 0 0 #3dbb8a;
}

.level-card--dark.level-card--l2 {
  box-shadow: 0 4px 14px rgba(240, 180, 41, 0.15), inset 4px 0 0 #f0b429;
}

.level-card--dark.level-card--l3 {
  box-shadow: 0 4px 14px rgba(155, 109, 215, 0.15), inset 4px 0 0 #9b6dd7;
}

.level-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.level-card--l1 .level-card__header {
  background: linear-gradient(180deg, #e8faf3 0%, #fff 100%);
}

.level-card--l2 .level-card__header {
  background: linear-gradient(180deg, #fff8e6 0%, #fff 100%);
}

.level-card--l3 .level-card__header {
  background: linear-gradient(180deg, #f5ecfc 0%, #fff 100%);
}

.level-card--dark.level-card--l1 .level-card__header {
  background: linear-gradient(180deg, rgba(61, 187, 138, 0.2) 0%, #343a40 100%);
}

.level-card--dark.level-card--l2 .level-card__header {
  background: linear-gradient(180deg, rgba(240, 180, 41, 0.15) 0%, #343a40 100%);
}

.level-card--dark.level-card--l3 .level-card__header {
  background: linear-gradient(180deg, rgba(155, 109, 215, 0.2) 0%, #343a40 100%);
}

.level-card__badge-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
}

.level-card__step {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
}

.level-card__badge {
  display: inline-block;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.65rem;
  border-radius: 6px;
  line-height: 1.2;
}

.level-card--l1 .level-card__badge {
  color: #0d6e4f;
  background: #b8ebd6;
}

.level-card--l2 .level-card__badge {
  color: #9a5f00;
  background: #ffe8a3;
}

.level-card--l3 .level-card__badge {
  color: #5b2d82;
  background: #e2cff5;
}

.level-card__title {
  font-size: 0.95rem;
  font-weight: 700;
}

.level-card__subtitle {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.1rem;
}

.level-card--dark .level-card__subtitle {
  color: #adb5bd;
}

.level-card__role {
  padding: 0.75rem 1rem;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
}

.level-card--dark .level-card__role {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.level-card__users {
  padding: 0.75rem 1rem 1rem;
  flex: 1;
}

.level-card__users-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin-bottom: 0.65rem;
}

.level-card--dark .level-card__users-label {
  color: #adb5bd;
}

.user-entry {
  padding: 0.75rem;
  margin-bottom: 0.65rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
}

.user-entry--dark {
  background: #3d444b;
  border-color: #6c757d;
}

.user-entry__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.user-entry__tag {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #495057;
  background: #e9ecef;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
}

.user-entry--dark .user-entry__tag {
  color: #f8f9fa;
  background: #495057;
}

.user-entry__remove {
  font-size: 0.8rem;
  color: #dc3545 !important;
}

.level-card__add-user {
  width: 100%;
}

.level-card__footer {
  margin-top: auto;
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
}

.level-card--dark .level-card__footer {
  background: rgba(0, 0, 0, 0.2);
  border-top-color: rgba(255, 255, 255, 0.08);
}

.level-card__footer-hint {
  font-size: 0.7rem;
  font-style: italic;
  color: #6c757d;
}

.level-card--dark .level-card__footer-hint {
  color: #adb5bd;
}

.multiselect-dark ::v-deep .multiselect__tags,
.multiselect-dark ::v-deep .multiselect__input,
.multiselect-dark ::v-deep .multiselect__single {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
  border-color: #6c757d !important;
}

.multiselect-dark ::v-deep .multiselect__content-wrapper,
.multiselect-dark ::v-deep .multiselect__content,
.multiselect-dark ::v-deep .multiselect__option {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
}

.multiselect-dark ::v-deep .multiselect__select:before,
.multiselect-dark ::v-deep .multiselect__placeholder {
  color: #f8f9fa !important;
}
</style>
